(function () {
  function formatMoneyCents(cents, format) {
    if (typeof cents !== "number" || Number.isNaN(cents) || !format || typeof format !== "string") {
      return "";
    }

    const amount = Math.max(0, cents) / 100;
    const amountNoDecimals = Math.round(amount).toString();
    const amountTwoDecimals = amount.toFixed(2);
    const amountWithCommaSeparator = amountTwoDecimals.replace(".", ",");
    const amountNoDecimalsWithCommaSeparator = amountWithCommaSeparator.split(",")[0];

    return format
      .replace(/\{\{amount\}\}/g, amountTwoDecimals)
      .replace(/\{\{amount_no_decimals\}\}/g, amountNoDecimals)
      .replace(/\{\{amount_with_comma_separator\}\}/g, amountWithCommaSeparator)
      .replace(
        /\{\{amount_no_decimals_with_comma_separator\}\}/g,
        amountNoDecimalsWithCommaSeparator,
      );
  }

  function coerceTierScalar(value) {
    if (value == null) {
      return "";
    }

    if (typeof value === "number") {
      return value;
    }

    if (typeof value === "object") {
      if ("value" in value && value.value != null) {
        return coerceTierScalar(value.value);
      }

      if ("handle" in value && value.handle != null) {
        return coerceTierScalar(value.handle);
      }
    }

    return value;
  }

  function variantMoneyCents(value) {
    const n = typeof value === "number" ? value : Number(value);

    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  }

  function pickTier(tiers, qty) {
    let best = null;
    let bestMin = -1;

    for (const tier of tiers || []) {
      const minQ = Number(coerceTierScalar(tier.min_quantity)) || 0;
      if (minQ > qty) {
        continue;
      }

      if (best == null || minQ > bestMin) {
        best = tier;
        bestMin = minQ;
      }
    }

    return best;
  }

  function getQuantity(sectionRoot) {
    const selectedOption = sectionRoot.querySelector(
      ".wt-quantity-widget__option--selected[data-quantity]",
    );
    if (selectedOption) {
      const selectedQuantity = parseInt(selectedOption.dataset.quantity, 10);
      if (Number.isFinite(selectedQuantity) && selectedQuantity >= 1) {
        return selectedQuantity;
      }
    }

    const input = sectionRoot.querySelector(
      "quantity-counter .js-counter-quantity, quantity-counter input[name='quantity']",
    );
    if (!input) {
      return 1;
    }

    const n = parseInt(input.value, 10);

    return Number.isFinite(n) && n >= 1 ? n : 1;
  }

  function getNaturalUnitCents(sectionRoot, config, lastVariant) {
    const variant =
      lastVariant ||
      (() => {
        const el = sectionRoot.querySelector(
          "variant-options [data-selected-variant]",
        );
        if (!el) {
          return config.fallbackVariant;
        }

        try {
          return JSON.parse(el.textContent.trim());
        } catch (e) {
          return config.fallbackVariant;
        }
      })();

    const baseFromVariant = variantMoneyCents(variant?.price);
    if (!variant || baseFromVariant == null) {
      return null;
    }

    const compareRaw = variantMoneyCents(variant.compare_at_price);

    return {
      base: baseFromVariant,
      compare: compareRaw != null ? compareRaw : 0,
    };
  }

  function buildPriceData(finalCents, compareCents, config) {
    const fmt = config.currencyCodeEnabled
      ? config.moneyWithCurrencyFormat
      : config.moneyFormat;

    return {
      price: formatMoneyCents(finalCents, fmt),
      compareAtPrice: formatMoneyCents(compareCents, fmt),
      priceRaw: finalCents,
      compareAtPriceRaw: compareCents,
    };
  }

  function aggregateMatchingCartLines(cart, variantId) {
    const vid = Number(variantId);
    let sumFinal = 0;
    let sumOrig = 0;
    let sumQty = 0;

    for (const item of cart.items || []) {
      if (item.variant_id !== vid) {
        continue;
      }

      sumFinal += item.final_line_price;
      sumOrig += item.original_line_price;
      sumQty += item.quantity;
    }

    if (sumQty === 0) {
      return null;
    }

    return {
      unitFinal: Math.round(sumFinal / sumQty),
      unitOrig: Math.round(sumOrig / sumQty),
    };
  }

  class SupplyDiscountController {
    constructor(config) {
      this.config = config;
      this.lastVariant = null;
      this.sectionIdStr = String(config.sectionId);
      this.sectionRoot = document.getElementById(
        `shopify-section-${this.sectionIdStr}`,
      );
      if (!this.sectionRoot) {
        return;
      }

      this.originalSectionIdStr = this._readOriginalSectionId();
      this._destroyed = false;
      this._recalculateQueued = false;
      this._cartFetchAbort = null;
      this._unsubscribers = [];

      this._onSectionUnload = (ev) => {
        const unloaded = ev.detail?.sectionId;
        if (unloaded != null && String(unloaded) === this.sectionIdStr) {
          this.destroy();
        }
      };

      this._bind();
      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        document.addEventListener("shopify:section:unload", this._onSectionUnload);
      }
      this.scheduleRecalculate();
    }

    _readOriginalSectionId() {
      const vo = this.sectionRoot.querySelector("variant-options");
      if (vo?.dataset?.originalSection) {
        return String(vo.dataset.originalSection);
      }

      const withAttr = this.sectionRoot.querySelector("[data-original-section-id]");
      if (withAttr?.dataset?.originalSectionId) {
        return String(withAttr.dataset.originalSectionId);
      }

      return null;
    }

    _bind() {
      this._unsubscribers.push(
        subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
          const sid = payload?.data?.sectionId;
          if (!this.sectionMatches(sid)) {
            return;
          }

          this.lastVariant = payload?.data?.variant || null;
          this.scheduleRecalculate();
        }),
      );

      this._unsubscribers.push(
        subscribe(PUB_SUB_EVENTS.quantityUpdate, (payload) => {
          if (!this.sectionMatches(payload?.data?.sectionId)) {
            return;
          }

          this.scheduleRecalculate();
        }),
      );

      this._unsubscribers.push(
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.scheduleRecalculate();
        }),
      );
    }

    sectionMatches(eventSectionId) {
      if (eventSectionId == null) {
        return false;
      }

      const ev = String(eventSectionId);
      if (ev === this.sectionIdStr) {
        return true;
      }

      if (this.originalSectionIdStr && ev === this.originalSectionIdStr) {
        return true;
      }

      return false;
    }

    scheduleRecalculate() {
      if (this._destroyed) {
        return;
      }

      if (this._recalculateQueued) {
        return;
      }

      this._recalculateQueued = true;
      requestAnimationFrame(() => {
        this._recalculateQueued = false;
        void this.recalculate();
      });
    }

    destroy() {
      if (this._destroyed) {
        return;
      }

      this._destroyed = true;
      this._cartFetchAbort?.abort();
      this._cartFetchAbort = null;

      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        document.removeEventListener("shopify:section:unload", this._onSectionUnload);
      }

      this._unsubscribers.forEach((unsub) => unsub());
      this._unsubscribers = [];
    }

    getCurrentVariantForPricing() {
      if (this.lastVariant) {
        return this.lastVariant;
      }

      const el = this.sectionRoot.querySelector(
        "variant-options [data-selected-variant]",
      );
      if (el) {
        try {
          return JSON.parse(el.textContent.trim());
        } catch (e) {}
      }

      return this.config.fallbackVariant;
    }

    recalculate() {
      if (this._destroyed) {
        return;
      }

      if (typeof window.updateProductPriceBlocks !== "function") {
        return;
      }

      const variant = this.getCurrentVariantForPricing();
      if (!variant?.id) {
        return;
      }

      // CreaWell: the header reflects the tier SELECTED on the page, never what is
      // already in the cart. The removed cart-based branch used to override the
      // selection with the cart's unit discount, so a fresh 1-box view showed the
      // cart's discount as a fake badge (e.g. "-50%"). Cart/checkout are unaffected.
      const natural = getNaturalUnitCents(
        this.sectionRoot,
        this.config,
        variant,
      );
      if (!natural || natural.base <= 0) {
        return;
      }

      const qty = getQuantity(this.sectionRoot);
      const tier = pickTier(this.config.tiers, qty);
      const percent = tier ? Number(coerceTierScalar(tier.discount_percent)) || 0 : 0;

      let finalCents = natural.base;
      let compareCents = natural.base;
      const rawCompare = natural.compare || 0;

      if (tier && percent > 0 && percent <= 100) {
        finalCents = Math.round((natural.base * (100 - percent)) / 100);
        compareCents = Math.max(natural.base, rawCompare);
      } else if (rawCompare > natural.base) {
        compareCents = rawCompare;
      }

      if (compareCents < finalCents) {
        compareCents = finalCents;
      }

      if (this._destroyed) {
        return;
      }

      window.updateProductPriceBlocks(
        this.sectionIdStr,
        buildPriceData(finalCents, compareCents, this.config),
      );
    }
  }

  function initSupplyDiscountDisplay() {
    document
      .querySelectorAll('script[type="application/json"][id^="SupplyWidgetConfig-"]')
      .forEach((script) => {
        try {
          const config = JSON.parse(script.textContent.trim());
          if (config.sectionId != null) {
            new SupplyDiscountController(config);
          }
        } catch (e) {
          console.warn("SupplyWidgetConfig parse error", e);
        }
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSupplyDiscountDisplay);
  } else {
    initSupplyDiscountDisplay();
  }
})();
